import { CommonModule, NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  TemplateRef,
  computed,
  contentChild,
  effect,
  input,
  output,
  signal,
  viewChild,
  OnInit,
  OnDestroy,
  Renderer2,
  inject
} from '@angular/core';

import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { phosphorDotsThreeVertical } from '@ng-icons/phosphor-icons/regular';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { Tracking } from '@app/shared/types/tracking';

import { OverlayDirective } from '../../directives/overlay/overlay.directive';
import {
  createPositionObserverWithCallback,
  type PositionObserver,
  type PositioningOptions,
  type PositionResult
} from '../../helpers/element-positioning';
import { SpinLoaderComponent } from '../spin-loader';

/**
 * `IconName` defaults to plain `string`, but a consumer can pass its own
 * icon-name union (e.g. `DropdownItem<'lucideTrash2' | 'lucideSend'>`) to get
 * autocomplete/type-checking on `icon` — `ui-dropdown` only ships its own
 * trigger icon (`phosphorDotsThreeVertical`); each consumer registers the
 * icons its items reference via its own `providers: [provideIcons({...})]`,
 * instead of every dropdown icon ever needed piling up in this shared
 * component.
 */
export interface DropdownItem<IconName extends string = string> {
  action?: () => void | Promise<unknown> | Observable<unknown>;
  name: string;
  disabled?: boolean;
  hidden?: boolean;
  /** Non-interactive info row (e.g. app version). */
  static?: boolean;
  tracking?: Tracking;
  icon?: IconName;
  /** Items sharing the same `group` are rendered together under a label divider. */
  group?: string;
}

interface DropdownContext {
  toggleExpanded: () => void;
  expanded: boolean;
}

@Component({
  selector: 'ui-dropdown',
  standalone: true,
  imports: [
    NgClass,
    CommonModule,
    NgIconComponent,
    OverlayDirective,
    SpinLoaderComponent
  ],
  providers: [provideIcons({ phosphorDotsThreeVertical })],
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss']
})
export class DropdownComponent implements OnInit, OnDestroy {
  readonly menuElementRef =
    viewChild<ElementRef<HTMLDivElement>>('menuElement');
  /** Inner node that actually scales/fades — see the template comment above it. */
  readonly menuContentRef =
    viewChild<ElementRef<HTMLDivElement>>('menuContent');
  readonly containerRef = viewChild<ElementRef<HTMLDivElement>>('container');
  readonly dropdownTrigger =
    contentChild<TemplateRef<unknown>>('dropdownTrigger');
  readonly dropdownContent =
    contentChild<TemplateRef<unknown>>('dropdownContent');

  protected readonly expanded = signal(null);
  protected readonly uniqueId = signal(
    Math.random().toString(36).substring(2, 9)
  );

  readonly items = input<DropdownItem[]>([]);
  readonly variant = input<'icon' | 'text' | 'both'>('text');
  readonly name = input<string>('Ações');
  readonly icon = input<string>('phosphorDotsThreeVertical');
  readonly size = input<'base' | 'lg'>('base');

  readonly toggle = output<{ expanded: boolean }>();

  readonly isExpanded = computed(() => !!this.expanded());
  readonly showMenuItems = computed(() => !!this.items()?.length);
  readonly loadingIndex = signal<number | null>(null);

  readonly renderItems = computed<DropdownItem[]>(() => {
    const result: DropdownItem[] = [];
    let lastGroup: string | undefined;

    for (const item of this.items()) {
      if (item.group !== undefined && item.group !== lastGroup) {
        result.push({ name: item.group, static: true });
      }
      lastGroup = item.group;
      result.push(item);
    }

    return result;
  });

  readonly hasCustomContent = computed(() => !!this.dropdownContent());

  readonly context = computed<DropdownContext>(() => ({
    toggleExpanded: this.toggleExpanded.bind(this),
    expanded: this.expanded()
  }));

  private positionObserver: PositionObserver | null = null;
  private readonly renderer = inject(Renderer2);

  constructor() {
    effect(() => {
      if (this.expanded() !== null) {
        requestAnimationFrame(() => {
          this.#setupPositioning();
        });
      }
    });
  }

  ngOnInit(): void {
    if (this.expanded() === null) {
      this.expanded.set(false);
    }
  }

  ngOnDestroy(): void {
    this.positionObserver?.destroy();
  }

  toggleExpanded(): void {
    this.expanded.update((prev) => !prev);

    this.toggle.emit({ expanded: this.expanded() });
  }

  onActionDispatch(item: DropdownItem, index: number): void {
    if (item.static || !item.action) {
      return;
    }

    const clearAndClose = (): void => {
      this.loadingIndex.set(null);
      this.expanded.update(() => false);
    };

    this.loadingIndex.set(index);
    const result = item.action();

    if (result instanceof Promise) {
      result.finally(clearAndClose).catch(() => undefined);
    } else if (
      result &&
      typeof (result as Observable<unknown>).subscribe === 'function'
    ) {
      (result as Observable<unknown>).pipe(finalize(clearAndClose)).subscribe();
    } else {
      clearAndClose();
    }
  }

  #setupPositioning(): void {
    const container = this.containerRef()?.nativeElement;
    const menu = this.menuElementRef()?.nativeElement;

    if (!container || !menu) {
      return;
    }

    // Cleanup previous observer
    this.positionObserver?.destroy();

    this.#setupDropdownPositioning(container, menu);
  }

  #setupDropdownPositioning(container: HTMLElement, menu: HTMLElement): void {
    // Sempre usar portal approach - mover para body com position fixed
    if (!document.body.contains(menu)) {
      document.body.appendChild(menu);
    }

    this.renderer.setStyle(menu, 'position', 'fixed');
    this.renderer.setStyle(menu, 'zIndex', '9999');

    const options: PositioningOptions = {
      preferredPlacement: 'bottom-right',
      offset: 2,
      viewportMargin: 8,
      // Menu sem seta: alinha pelas bordas do trigger, não pelo centro
      horizontalAnchor: 'edge',
      fallbackPlacements: [
        'bottom-left',
        'top-right',
        'top-left',
        'bottom',
        'top'
      ]
    };

    this.positionObserver = createPositionObserverWithCallback(
      container,
      menu,
      options,
      (position: PositionResult) => {
        this.#applyPosition(position);
      }
    );
  }

  #applyPosition(position: PositionResult): void {
    const menu = this.menuElementRef()?.nativeElement;
    if (!menu) {
      return;
    }

    this.renderer.setStyle(menu, 'top', `${position.top}px`);
    this.renderer.setStyle(menu, 'left', `${position.left}px`);

    const content = this.menuContentRef()?.nativeElement;
    if (content) {
      this.renderer.setStyle(
        content,
        'transformOrigin',
        position.origin.replace('origin-', '')
      );
    }
  }
}
